import type { MouseEvent, ReactElement } from 'react';
import { useDisplayTimeZone } from '@/hooks/useDisplayTimeZone';
import { Check } from 'lucide-react';
import type {
  ToolApproval,
  ToolApprovalScope,
} from '@kirin/api-client/contracts/shadow-ai-tool-approvals';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useToolApprovalMutation } from '@/hooks/api/useShadowAiToolApprovals';
import { formatApprovedBy } from './toolApprovalFormat';

// Cell controls sit inside clickable / expandable rows — keep their clicks
// (including ones landing on a disabled button, which has pointer-events:none)
// from also toggling the row.
const stopRowClick = (event: MouseEvent): void => {
  event.stopPropagation();
};

/** Wraps `children` in a tooltip when `text` is given; otherwise returns it as is. */
function WithTooltip({
  text,
  children,
}: {
  text?: string;
  children: ReactElement;
}): ReactElement {
  if (!text) return children;
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent>{text}</TooltipContent>
    </Tooltip>
  );
}

function ApprovedBadge(): ReactElement {
  return (
    <span className="inline-flex items-center gap-1 rounded-full border border-status-success-border bg-status-success-background px-2 py-0.5 text-xs font-medium text-status-success-foreground whitespace-nowrap">
      <Check className="h-3 w-3" aria-hidden="true" />
      Approved
    </span>
  );
}

function ApprovedSummary({
  approval,
  children,
}: {
  approval: ToolApproval;
  children?: ReactElement;
}): ReactElement {
  const timeZone = useDisplayTimeZone();
  return (
    <div
      className="flex flex-wrap items-center gap-x-2 gap-y-1"
      data-testid="tool-approval-approved"
    >
      <ApprovedBadge />
      <span className="text-xs text-muted-foreground">
        {formatApprovedBy(approval, timeZone)}
      </span>
      {children}
    </div>
  );
}

/**
 * Read-only Approval cell (members, and anyone without the org write role). `approval` null/undefined → "Not approved".
 */
export function ToolApprovalStatus({
  approval,
}: {
  approval: ToolApproval | null | undefined;
}): ReactElement {
  if (!approval) {
    return <span className="text-xs text-muted-foreground">Not approved</span>;
  }
  return <ApprovedSummary approval={approval} />;
}

export interface ToolApprovalActionCellProps {
  /** null = the caller's personal account (KIR-4864). */
  organizationId: string | null;
  scope: ToolApprovalScope;
  toolId: string;
  approval: ToolApproval | null | undefined;
  /** Support: render the controls but disabled ("disable, don't hide"). */
  disabled?: boolean;
  /** KIR-4997: hover copy for the Approve / Unapprove buttons (Running Now). */
  tooltips?: ToolApprovalActionTooltips;
}

export interface ToolApprovalActionTooltips {
  approve: string;
  unapprove: string;
}

/**
 * Approval cell for Owners / Managers (Support gets it `disabled`) and for the
 * owner of a personal account (KIR-4864): an outlined
 * green "Approve" button when not approved; otherwise the approved summary plus
 * an "Unapprove" link-button.
 */
export function ToolApprovalActionCell({
  organizationId,
  scope,
  toolId,
  approval,
  disabled = false,
  tooltips,
}: ToolApprovalActionCellProps): ReactElement {
  const mutation = useToolApprovalMutation(organizationId);
  const isBusy = disabled || mutation.isPending;

  const run = (action: 'approve' | 'unapprove'): void => {
    mutation.mutate({ action, scope, toolIds: [toolId] });
  };

  if (!approval) {
    return (
      <div className="inline-flex" onClick={stopRowClick}>
        <WithTooltip text={tooltips?.approve}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={isBusy}
            aria-busy={mutation.isPending}
            onClick={() => run('approve')}
            className={cn(
              'h-7 gap-1 rounded-md border-status-success-emphasis bg-background px-2.5 text-xs font-medium text-status-success-foreground shadow-none',
              'hover:border-status-success-emphasis hover:bg-status-success-background hover:text-status-success-foreground'
            )}
          >
            <Check className="h-3.5 w-3.5" aria-hidden="true" />
            Approve
          </Button>
        </WithTooltip>
      </div>
    );
  }

  return (
    <div onClick={stopRowClick}>
      <ApprovedSummary approval={approval}>
        <WithTooltip text={tooltips?.unapprove}>
          <Button
            type="button"
            variant="link"
            size="sm"
            disabled={isBusy}
            aria-busy={mutation.isPending}
            onClick={() => run('unapprove')}
            className="h-auto p-0 text-xs font-medium text-blue-500 dark:text-blue-400"
          >
            Unapprove
          </Button>
        </WithTooltip>
      </ApprovedSummary>
    </div>
  );
}
